// SidebarSchool — the institution at the top of the left menu, pure.
//
// One institution: its mark and name, not a button (spec §4). Two or more,
// with switching wired: a button with a chevron opening "Your institutions"
// (a check on the current one). Long names truncate; the tooltip has the
// full name. Folded to the rail, only the mark shows, on the icon axis.

export { SidebarSchool };

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Icon } from "../Icon/Icon.js";
import { Menu, type MenuItem } from "../Menu/Menu.js";
import { Tooltip } from "../Tooltip/Tooltip.js";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden.js";
import { markOf, type SchoolSwitcher } from "./sidebarEntries.js";

const Mark = ({ name }: { readonly name: string }) => (
    <span className="bilda-sidebar__mark" aria-hidden="true">
        {markOf(name)}
    </span>
);

const SidebarSchool = ({
    school,
    expanded,
}: {
    readonly school: SchoolSwitcher;
    readonly expanded: boolean;
}): ReactNode => {
    const { t } = useLingui();
    switch (school.type) {
        case "none":
            return null;
        case "single": {
            const { name } = school.current;
            return (
                <Tooltip content={name} side="right">
                    <div className="bilda-sidebar__school">
                        <Mark name={name} />
                        {expanded ? (
                            <span className="bilda-sidebar__school-name">
                                {name}
                            </span>
                        ) : (
                            <VisuallyHidden>{name}</VisuallyHidden>
                        )}
                    </div>
                </Tooltip>
            );
        }
        case "switcher": {
            const { current, all, onSwitch } = school;
            const name = current.name;
            return (
                <Menu
                    trigger={
                        <button
                            type="button"
                            className="bilda-sidebar__school"
                            aria-label={t`${name}, switch institution`}
                        >
                            <Mark name={current.name} />
                            {expanded ? (
                                <>
                                    <span className="bilda-sidebar__school-name">
                                        {current.name}
                                    </span>
                                    <span
                                        className="bilda-sidebar__school-chevron"
                                        aria-hidden="true"
                                    >
                                        <Icon name="chevrons-up-down" />
                                    </span>
                                </>
                            ) : null}
                        </button>
                    }
                    items={[
                        { _tag: "label", label: t`Your institutions` },
                        ...all.map(
                            (institution): MenuItem => ({
                                _tag: "item",
                                label: institution.name,
                                icon: <Mark name={institution.name} />,
                                checked: institution.id === current.id,
                                onSelect: () => onSwitch(institution.id),
                            }),
                        ),
                    ]}
                />
            );
        }
        default:
            return exhaustSwitchCase(school);
    }
};
